import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, Trash2 } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Field } from '@vnetwork/ui/forms';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  IconButton,
} from '@vnetwork/ui/primitives';
import { useToast } from '@vnetwork/ui/feedback';
import { useCreateWaapWebsite } from '@/api/waap';
import { WaapConsoleBreadcrumbs } from '@/pages/services/waap/WaapConsoleBreadcrumbs';
import { CheckboxField } from '@/pages/services/waap/components/CheckboxField';
import { trackFeatureAction } from '@/lib/analytics-events';
import styles from './AddWebsite.module.css';

const MAX_ORIGINS = 5;

interface OriginRow {
  protocol: string; // 'https://' | 'http://'
  host: string;
}

const EMPTY_ORIGIN: OriginRow = { protocol: 'https://', host: '' };

/**
 * Add WAAP Website — the partner's single create entry point. WAAP is reverse-
 * create: this posts a domain bound to the eagle provider, and the backend
 * creates the WAAP Website first then auto-provisions the backing CDN service
 * (see servicemgmt conversant CreateDomain). The user only deals with the website.
 */
export function AddWebsite() {
  const navigate = useNavigate();
  const { serviceId } = useParams<{ serviceId: string }>();
  const { show } = useToast();
  const base = `/services/waap/${serviceId}`;

  const [domain, setDomain] = useState('');
  const [origins, setOrigins] = useState<OriginRow[]>([{ ...EMPTY_ORIGIN }]);
  const [altInput, setAltInput] = useState('');
  const [altDomains, setAltDomains] = useState<string[]>([]);
  const [http2, setHttp2] = useState(false);
  const [redirectHttps, setRedirectHttps] = useState(true);

  const setOrigin = (i: number, patch: Partial<OriginRow>) =>
    setOrigins((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addOrigin = () =>
    setOrigins((rows) => (rows.length < MAX_ORIGINS ? [...rows, { ...EMPTY_ORIGIN }] : rows));
  const removeOrigin = (i: number) =>
    setOrigins((rows) => (rows.length > 1 ? rows.filter((_, idx) => idx !== i) : rows));

  const addAlt = () => {
    const v = altInput.trim();
    if (v && !altDomains.includes(v)) setAltDomains((d) => [...d, v]);
    setAltInput('');
  };

  const create = useCreateWaapWebsite(serviceId, {
    onSuccess: () => {
      // track feature engagement
      trackFeatureAction('waap', 'website', 'create');
      show({ tone: 'success', title: 'Website created' });
      navigate(`${base}/websites`);
    },
    onError: (e) =>
      show({
        tone: 'danger',
        title: 'Could not create website',
        description: (e as { response?: { data?: { message?: string } } })?.response?.data?.message,
      }),
  });

  const canSubmit =
    domain.trim().length > 0 &&
    origins.some((o) => o.host.trim().length > 0) &&
    !!serviceId &&
    !create.isPending;

  const submit = () => {
    const name = domain.trim();
    const urls = origins
      .filter((o) => o.host.trim())
      .map((o) => `${o.protocol}${o.host.trim()}`);
    // WAAP reverse-create: the BE creates the WAAP website (vendor auto-provisions
    // the backing CDN service) and persists the domain + binding.
    create.mutate({
      domain: name,
      origins: urls,
      alternate_domain_names: altDomains,
      http2,
      redirect_http_to_https: redirectHttps,
    });
  };

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={
          <WaapConsoleBreadcrumbs trail={[{ label: 'Websites', slug: 'websites' }, { label: 'Add Website' }]} />
        }
        title="Add Website"
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-5)' }}>
        <Card flat>
          <CardHeader>
            <CardTitle>Domain</CardTitle>
            <CardDescription>The website you want to protect with WAAP.</CardDescription>
          </CardHeader>
          <CardBody>
            <Field label="Domain Name" required description="Valid characters: 0-9, a-z, - and . (e.g. www.example.com).">
              {(props) => (
                <Input
                  {...props}
                  value={domain}
                  placeholder="www.example.com"
                  onChange={(e) => setDomain(e.target.value)}
                />
              )}
            </Field>
          </CardBody>
        </Card>

        <Card flat>
          <CardHeader>
            <CardTitle>Origin URL</CardTitle>
            <CardDescription>Where the edge fetches uncached content from. Add 1-5 origins (domain or IPv4).</CardDescription>
          </CardHeader>
          <CardBody>
            <div className={styles.originList}>
              {origins.map((o, i) => (
                <div key={i} className={styles.originRow}>
                  <Field label={i === 0 ? 'Protocol' : ''}>
                    {() => (
                      <Select value={o.protocol} onValueChange={(v) => setOrigin(i, { protocol: v })}>
                        <SelectTrigger className={styles.protocolSelect}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="https://">https://</SelectItem>
                          <SelectItem value="http://">http://</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </Field>
                  <Field label={i === 0 ? 'Origin' : ''}>
                    {(props) => (
                      <Input
                        {...props}
                        value={o.host}
                        placeholder="origin.example.com"
                        onChange={(e) => setOrigin(i, { host: e.target.value })}
                      />
                    )}
                  </Field>
                  <IconButton
                    aria-label="Remove origin"
                    variant="ghost"
                    disabled={origins.length === 1}
                    onClick={() => removeOrigin(i)}
                  >
                    <Trash2 size={16} />
                  </IconButton>
                </div>
              ))}
              <div>
                <Button
                  variant="secondary"
                  size="sm"
                  startIcon={<Plus size={14} />}
                  disabled={origins.length >= MAX_ORIGINS}
                  onClick={addOrigin}
                >
                  Add Origin URL
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>

        <Card flat>
          <CardHeader>
            <CardTitle>Multi Domains</CardTitle>
            <CardDescription>Alternate domains share configuration and cache policy with the primary domain. Certificates are configured separately.</CardDescription>
          </CardHeader>
          <CardBody>
            <div className={styles.altRow}>
              <Field label="Alternate Domain Name">
                {(props) => (
                  <Input
                    {...props}
                    value={altInput}
                    placeholder="www.example.net"
                    onChange={(e) => setAltInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addAlt();
                      }
                    }}
                  />
                )}
              </Field>
              <Button variant="secondary" size="sm" startIcon={<Plus size={14} />} onClick={addAlt}>
                Add Alternate Domain
              </Button>
            </div>
            {altDomains.length > 0 && (
              <div className={styles.altList}>
                {altDomains.map((d) => (
                  <div key={d} className={styles.altItem}>
                    <span>{d}</span>
                    <IconButton
                      aria-label="Remove alternate domain"
                      variant="ghost"
                      onClick={() => setAltDomains((list) => list.filter((x) => x !== d))}
                    >
                      <Trash2 size={16} />
                    </IconButton>
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>

        <Card flat>
          <CardHeader>
            <CardTitle>Other</CardTitle>
          </CardHeader>
          <CardBody>
            <div className={styles.otherGrid}>
              <CheckboxField
                checked={http2}
                onChange={setHttp2}
                label="Enable HTTP/2"
                description="Allow clients to connect over HTTP/2. Configure an SSL certificate first."
              />
              <CheckboxField
                checked={redirectHttps}
                onChange={setRedirectHttps}
                label="Redirect HTTP to HTTPS"
                description="Force plain HTTP requests to HTTPS with the same path/query."
              />
            </div>
          </CardBody>
        </Card>

        <div className={styles.footer}>
          <Button variant="secondary" onClick={() => navigate(`${base}/websites`)}>
            Cancel
          </Button>
          <Button disabled={!canSubmit} onClick={submit}>
            {create.isPending ? 'Creating…' : 'Save'}
          </Button>
        </div>
      </div>
    </Container>
  );
}
